import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Cloud, RefreshCw, Users, CheckCircle2, XCircle, ChevronDown, ChevronUp } from "lucide-react";
import { API } from "../../config";

export const SalesforceCustomerSyncWidget = () => {
  const [summary, setSummary] = useState(null);
  const [starting, setStarting] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const pollRef = useRef(null);

  const fetchSummary = async () => {
    try {
      const r = await axios.get(`${API}/salesforce/customers-sync-summary`);
      setSummary(r.data);
      return r.data;
    } catch {
      return null;
    }
  };

  const startPolling = () => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const data = await fetchSummary();
      if (data?.last_job?.status !== "running") {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (data?.last_job) {
          const j = data.last_job;
          toast.success(
            `Sync massiva completata: ${j.created} creati, ${j.duplicates} duplicati, ${j.failed} falliti`
          );
        }
      }
    }, 2500);
  };

  useEffect(() => {
    fetchSummary().then((data) => {
      if (data?.last_job?.status === "running") startPolling();
    });
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  const syncAll = async () => {
    setStarting(true);
    try {
      const r = await axios.post(`${API}/salesforce/sync-all-customers`);
      if (r.data.job_id) {
        toast.info(r.data.message);
        await fetchSummary();
        startPolling();
      } else {
        toast.success(r.data.message);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore avvio sincronizzazione");
    } finally {
      setStarting(false);
    }
  };

  if (!summary) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="sf-customer-sync-widget">
        <div className="spinner mx-auto" />
      </div>
    );
  }

  const job = summary.last_job;
  const running = job?.status === "running";
  const progress = running && job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <div
      className="bg-gradient-to-br from-[#0f1422] via-[#0a0d18] to-[#0f1422] border border-sky-500/20 rounded-2xl p-6 space-y-5"
      data-testid="sf-customer-sync-widget"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-white font-semibold flex items-center gap-2">
          <Cloud size={18} className="text-sky-400" />
          Sync Salesforce Clienti
        </h3>
        <button
          onClick={syncAll}
          disabled={starting || running || summary.unsynced === 0}
          className="btn-lojack flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="sf-sync-all-btn"
        >
          {starting || running ? (
            <RefreshCw size={15} className="animate-spin" />
          ) : (
            <Cloud size={15} />
          )}
          {running ? "In corso..." : `Sincronizza tutti (${summary.unsynced})`}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gray-800/40 rounded-xl p-4" data-testid="sf-stat-unsynced">
          <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
            <Users size={12} /> Da sincronizzare
          </div>
          <p className={`text-2xl font-bold ${summary.unsynced > 0 ? "text-lojack-clause-400" : "text-gray-400"}`}>
            {summary.unsynced}
          </p>
        </div>
        <div className="bg-gray-800/40 rounded-xl p-4" data-testid="sf-stat-synced">
          <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
            <CheckCircle2 size={12} /> Sincronizzati
          </div>
          <p className="text-2xl font-bold text-lojack-blue-400">{summary.synced}</p>
        </div>
        <div className="bg-gray-800/40 rounded-xl p-4" data-testid="sf-stat-failed">
          <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
            <XCircle size={12} /> Con errori
          </div>
          <p className={`text-2xl font-bold ${summary.failed > 0 ? "text-red-400" : "text-gray-400"}`}>
            {summary.failed}
          </p>
        </div>
      </div>

      {running && (
        <div data-testid="sf-sync-progress">
          <div className="flex justify-between text-xs text-gray-400 mb-1">
            <span>Sincronizzazione in corso...</span>
            <span>
              {job.processed}/{job.total} ({progress}%)
            </span>
          </div>
          <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-sky-400 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {job && !running && (
        <div className="text-xs text-gray-500 space-y-2" data-testid="sf-last-job-summary">
          <p>
            Ultima sync massiva: {new Date(job.started_at).toLocaleString("it-IT")} —{" "}
            <span className="text-lojack-blue-400">{job.created} creati</span>,{" "}
            <span className="text-sky-400">{job.duplicates} duplicati</span>,{" "}
            <span className={job.failed > 0 ? "text-red-400" : "text-gray-500"}>{job.failed} falliti</span>
          </p>
          {job.errors?.length > 0 && (
            <div>
              <button
                onClick={() => setShowErrors((v) => !v)}
                className="flex items-center gap-1 text-red-400 hover:text-red-300 transition-colors"
                data-testid="sf-sync-errors-toggle"
              >
                {showErrors ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {job.errors.length} errori — dettagli
              </button>
              {showErrors && (
                <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {job.errors.map((e, i) => (
                    <li key={i} className="bg-red-900/10 border border-red-800/30 rounded-lg px-3 py-1.5">
                      <span className="text-white">{e.name}</span>:{" "}
                      <span className="text-red-400">{e.error}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      <p className="text-gray-600 text-xs">
        Invia a Salesforce tutti i clienti con contratto firmato non ancora sincronizzati.
        I clienti già presenti nel CRM vengono riconosciuti come duplicati (stesso member number).
      </p>
    </div>
  );
};
